/**
 * DEMO · Idea 3 — Números generadores.
 *
 * Para un renglón (muro exterior de la HP de ejemplo): la memoria de cálculo por nivel y pieza, con sus
 * medidas y la operación, más el croquis del elemento resaltado (captura del visor).
 * Real (del modelo): qué muros entran, su largo, alto y área neta (medición de ci) y su nivel. La pieza se
 * nombra como viene en el modelo: sin ejes ni fachadas deducidos. De ejemplo: el concepto y su precio.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@fluentui/react-components';
import { Print20Regular } from '@fluentui/react-icons';
import { vanosEnPalabras as vanosEnPalabrasDelPlano } from '@supernova/shared';
import { DemoVisor3D, type Pintura, type VisorDemoHandle } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel, estiloTabla } from '../../components/demos/DemoMarco.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import {
  HP_EJEMPLO,
  cantidadDe,
  cantidadEstimada,
  coincide,
  coloresDemo,
  crearContexto,
  nombreClase,
  num2,
  pesos,
  r2cm,
} from '../../components/demos/logicaDemo.js';
import { conceptosDelModelo } from '../../components/demos/avanceObra.js';
import catalogo from '../../components/demos/datos/catalogo.json';
import datos from '../../components/demos/datos/numeros-generadores.json';

const FICHA = catalogo.demos.find((d) => d.id === 'numeros-generadores')!;
const CONCEPTO = HP_EJEMPLO.conceptos.find((c) => c.id === datos.concepto)!;

interface Fila {
  el: ElementoDemo;
  nivel: string;
  largo: number;
  alto: number;
  bruta: number;
  vanos: number;
  neta: number;
  estimado: boolean;
  palabras: string;
}

function nombreNivel(orden: number | null, total: number): string {
  if (orden == null) return 'sin piso';
  if (orden === 0) return 'planta baja';
  return total === 2 ? 'planta alta' : `nivel ${orden + 1}`;
}

/** "2 ventanas de 2.00 × 1.20 y 1 puerta de 1.01 × 2.01", de las piezas que llenan los vanos del muro. */
function vanosEnPalabras(e: ElementoDemo, modelo: ModeloDemo): string {
  // La misma frase que el número generador real (@supernova/shared): una sola regla para los dos.
  const vanos = e.alojados.flatMap((id) => {
    const x = modelo.porClave.get(`${e.fuente}:${id}`);
    if (!x?.caja) return [];
    const ancho = Math.max(x.caja.max[0] - x.caja.min[0], x.caja.max[2] - x.caja.min[2]);
    return [{ nombre: nombreClase(x.clase), ancho, alto: x.caja.max[1] - x.caja.min[1] }];
  });
  return vanosEnPalabrasDelPlano(vanos);
}

export function DemoNumerosGeneradores() {
  const visor = useRef<VisorDemoHandle>(null);
  const [modelo, setModelo] = useState<ModeloDemo | null>(null);
  const [foco, setFoco] = useState<string | null>(null);
  const [croquis, setCroquis] = useState<string | null>(null);
  const colores = useMemo(() => coloresDemo(), []);

  const filas = useMemo<Fila[]>(() => {
    if (!modelo) return [];
    const ctx = crearContexto(modelo.elementos);
    const muros = modelo.elementos.filter((e) => coincide(e, CONCEPTO.selector, ctx));
    const nNiveles = modelo.niveles.length;
    return muros
      .map((el) => {
        const largo = cantidadDe(el, 'longitud');
        const alto = el.caja ? el.caja.max[1] - el.caja.min[1] : 0;
        const bruta = largo * alto;
        const neta = cantidadDe(el, 'area');
        return {
          el,
          nivel: el.nivelOrden != null ? `Nivel ${el.nivelOrden + 1} · ${nombreNivel(el.nivelOrden, nNiveles)}${el.nivelNombre ? ` (${el.nivelNombre})` : ''}` : 'Sin nivel',
          largo,
          alto,
          bruta,
          vanos: Math.max(0, bruta - neta),
          neta,
          estimado: cantidadEstimada(el, 'area') || cantidadEstimada(el, 'longitud'),
          palabras: (() => {
            const donde = `${el.nombre || 'Muro'}, ${nombreNivel(el.nivelOrden, nNiveles)}`;
            const vanos = vanosEnPalabras(el, modelo);
            return `${donde}: ${num2(largo)} m de largo × ${num2(alto)} m de alto${vanos ? `, menos ${vanos}` : ''}`;
          })(),
        };
      })
      .sort((a, b) => a.nivel.localeCompare(b.nivel) || a.palabras.localeCompare(b.palabras, 'es', { numeric: true }));
  }, [modelo]);
  const total = filas.reduce((s, f) => s + f.neta, 0);
  // Lo que dice la hoja de presupuesto para este concepto (misma medición).
  const enHoja = useMemo(() => (modelo ? (conceptosDelModelo(modelo).find((x) => x.c.id === CONCEPTO.id)?.cantidad ?? 0) : 0), [modelo]);
  const cuadra = r2cm(enHoja) === r2cm(total);
  const porNivel = [...new Set(filas.map((f) => f.nivel))].map((n) => ({ n, fs: filas.filter((f) => f.nivel === n) }));
  const clavesRenglon = useMemo(() => new Set(filas.map((f) => f.el.clave)), [filas]);

  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        if (!el) return { visible: false };
        const resaltado = foco ? clave === foco : clavesRenglon.has(clave);
        return resaltado ? { color: colores.acento, opacidad: 1 } : { color: colores.neutro, opacidad: 0.12 };
      },
    [foco, clavesRenglon, colores],
  );

  // Croquis: al cambiar lo resaltado, encuadrar y tomar la imagen del visor.
  useEffect(() => {
    if (!modelo) return;
    visor.current?.encuadrar(foco ? [foco] : [...clavesRenglon]);
    const t = setTimeout(() => setCroquis(visor.current?.capturar() ?? null), datos.croquis.esperaMs);
    return () => clearTimeout(t);
  }, [foco, modelo, clavesRenglon]);

  const filaFoco = filas.find((f) => f.el.clave === foco);

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--fs-400)', maxWidth: 900, lineHeight: 1.45 }}>
        El número generador es la hoja que demuestra de dónde sale cada cantidad de tu presupuesto. La piden la dependencia o tu cliente
        para aceptarte las cantidades y para pagarte cada estimación.
      </p>
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #hoja-generador, #hoja-generador * { visibility: visible !important; color: #000 !important; background: transparent !important; border-color: #999 !important; }
        #hoja-generador { position: absolute; left: 0; top: 0; width: 100%; padding: 12mm; }
        #hoja-generador .no-imprimir { display: none !important; }
        @page { size: letter landscape; }
      }`}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.7fr) minmax(0, 1.6fr)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DemoVisor3D
            ref={visor}
            fuentes={[{ clave: 'a', url: '/demos/fzk-haus-a.ifc' }]}
            onListo={(m) => setModelo(m.a ?? null)}
            pintar={pintar}
            onPick={(c) => setFoco(c && clavesRenglon.has(c) ? c : null)}
            alto={420}
          />
          <NotaEjemplo>Clic en un renglón del generador (o en un muro) para su croquis; clic otra vez para verlos todos.</NotaEjemplo>
        </div>

        <div id="hoja-generador">
          <Panel
            titulo="Número generador"
            extra={
              <Button className="no-imprimir" icon={<Print20Regular />} onClick={() => window.print()} disabled={!filas.length}>
                Imprimir o guardar PDF
              </Button>
            }
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>Obra: {datos.obra}</div>
                <div style={{ fontSize: 'var(--fs-400)', lineHeight: 1.4, marginTop: 4 }}>
                  <b>Concepto:</b> {CONCEPTO.descripcion} <span style={{ color: 'var(--text-tertiary)' }}>({CONCEPTO.clave})</span>
                  <br />
                  <b>Cantidad en tu presupuesto:</b> {num2(enHoja)} {CONCEPTO.unidad} · <b>Aquí está la cuenta</b>
                </div>
              </div>
              <div style={{ flex: '0 0 220px' }}>
                <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)', marginBottom: 4 }}>
                  Croquis {filaFoco ? `· ${filaFoco.palabras.split(':')[0]}` : '· todos los muros del renglón'}
                </div>
                {croquis ? (
                  <img src={croquis} alt="Croquis del tramo resaltado" style={{ width: '100%', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }} />
                ) : (
                  <div style={{ height: 140, background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)' }} />
                )}
              </div>
            </div>
            <table style={estiloTabla.tabla}>
              <thead>
                <tr>
                  <th style={estiloTabla.th}>De dónde sale, en palabras</th>
                  <th style={estiloTabla.th}>Operación</th>
                  <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Cantidad</th>
                </tr>
              </thead>
              {porNivel.map(({ n, fs }) => (
                <tbody key={n}>
                  <tr>
                    <td colSpan={2} style={{ ...estiloTabla.td, fontWeight: 700, background: 'var(--bg-raised)' }}>
                      {n}
                    </td>
                    <td style={{ ...estiloTabla.num, fontWeight: 700, background: 'var(--bg-raised)' }}>
                      {num2(fs.reduce((s, f) => s + f.neta, 0))}
                    </td>
                  </tr>
                  {fs.map((f) => (
                    <tr
                      key={f.el.clave}
                      onClick={() => setFoco(foco === f.el.clave ? null : f.el.clave)}
                      style={{ cursor: 'pointer', background: foco === f.el.clave ? 'var(--accent-weak)' : undefined }}
                    >
                      <td style={estiloTabla.td}>
                        {f.palabras}
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          Pieza del modelo «{f.el.nombre || `#${f.el.eid}`}»
                          {f.estimado && <span style={{ color: 'var(--warn)' }}> · medido del dibujo</span>}
                        </div>
                      </td>
                      <td style={{ ...estiloTabla.td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {num2(f.largo)} × {num2(f.alto)} = {num2(f.bruta)}
                        {f.vanos > 0 ? ` − ${num2(f.vanos)} desc.` : ''}
                      </td>
                      <td style={{ ...estiloTabla.num, fontWeight: 600 }}>{num2(f.neta)}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ ...estiloTabla.td, fontWeight: 700 }}>
                    Total de la cuenta{' '}
                    <span style={{ fontWeight: 500, color: cuadra ? 'var(--ok)' : 'var(--bad)' }}>
                      {cuadra ? '· cuadra con tu hoja de presupuesto' : `· no cuadra con tu hoja (${num2(enHoja)})`}
                    </span>
                  </td>
                  <td style={{ ...estiloTabla.num, fontWeight: 700 }}>
                    {num2(total)} {CONCEPTO.unidad}
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} style={{ ...estiloTabla.td, color: 'var(--text-secondary)' }}>
                    Importe con el precio de ejemplo de {pesos(CONCEPTO.pu)}/{CONCEPTO.unidad}
                  </td>
                  <td style={estiloTabla.num}>{pesos(total * CONCEPTO.pu)}</td>
                </tr>
              </tfoot>
            </table>
            <NotaEjemplo>
              Cantidad = área neta del muro medida en el modelo. "desc." (descuentos) es largo × alto menos esa área: vanos de puertas y
              ventanas, la forma del muro (los piñones del techo) y los encuentros con otros muros. Cada pieza va con el nombre que trae el modelo. {datos.aviso}
            </NotaEjemplo>
          </Panel>
        </div>
      </div>
    </DemoMarco>
  );
}
